import { useState } from 'react';
import { ArrowLeft, Camera, Check, X, AlertTriangle } from 'lucide-react';
import { useProofPhotos } from '../../hooks/useProofPhotos';
import { useRiderLocationSync } from '../../hooks/useRiderLocationSync';

interface PickupConfirmationScreenProps {
  jobId: string;
  riderId?: string;
  pickupAddress: string;
  onBack: () => void;
  onConfirm: (data: { photos: string[]; notes?: string }) => void | Promise<void>;
}

export function PickupConfirmationScreen({
  jobId,
  riderId,
  pickupAddress,
  onBack,
  onConfirm,
}: PickupConfirmationScreenProps) {
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const MAX_PHOTOS = 3;
  const { photos, error, fileInputRef, handleFileChange, openPicker, deletePhoto } =
    useProofPhotos(MAX_PHOTOS);

  useRiderLocationSync(jobId, riderId, true);

  const handleConfirm = async () => {
    if (photos.length === 0 || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onConfirm({
        photos: photos.map((p) => p.uri),
        notes: notes || undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="absolute inset-0 flex flex-col bg-gray-50 overflow-hidden">
      <div className="shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <button
          onClick={onBack}
          className="w-12 h-12 flex items-center justify-center hover:bg-gray-100 rounded-xl transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft className="w-6 h-6 text-[#111111]" />
        </button>
        <div>
          <h1 className="text-[18px] text-[#111111]">Confirm Pickup</h1>
          <p className="text-[12px] text-[#6B6B6B]">Order #{jobId}</p>
        </div>
      </div>

      <div
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-4"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <div className="bg-[#E0001B]/10 rounded-[16px] p-4 border border-[#E0001B]/20">
          <p className="text-[12px] text-[#E0001B] mb-1">PICKUP LOCATION</p>
          <p className="text-[14px] text-[#111111]">{pickupAddress}</p>
        </div>

        <div className="bg-white rounded-[16px] p-4">
          <h2 className="text-[16px] text-[#111111] mb-3">Before You Leave</h2>
          <div className="space-y-3">
            {[
              'Verify package details',
              'Check for visible damage',
              'Ensure package is sealed',
              'Take clear photos',
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full border-2 border-[#00B277] flex items-center justify-center">
                  <Check className="w-3 h-3 text-[#00B277]" />
                </div>
                <span className="text-[14px] text-[#111111]">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileChange}
            className="hidden"
            aria-hidden
          />

          <div className="flex items-center justify-between mb-2">
            <label className="text-[14px] text-[#111111]">
              Package Photos <span className="text-[#E02020]">*</span>
            </label>
            <span className="text-[12px] text-[#6B6B6B]">
              {photos.length}/{MAX_PHOTOS}
            </span>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {photos.map((photo) => (
              <div key={photo.id} className="aspect-square relative">
                <img
                  src={photo.uri}
                  alt="Pickup proof"
                  className="w-full h-full object-cover rounded-[12px] border border-gray-200"
                />
                <button
                  type="button"
                  onClick={() => deletePhoto(photo.id)}
                  className="absolute -top-2 -right-2 w-7 h-7 bg-[#E02020] rounded-full text-white flex items-center justify-center shadow-md hover:bg-[#C00015] transition-colors"
                  aria-label="Delete photo"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="absolute bottom-1 left-1 px-2 py-0.5 bg-black/60 rounded text-[10px] text-white backdrop-blur">
                  {new Date(photo.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              </div>
            ))}

            {photos.length < MAX_PHOTOS && (
              <button
                type="button"
                onClick={openPicker}
                className="aspect-square border-2 border-dashed border-gray-300 rounded-[12px] flex flex-col items-center justify-center gap-1 hover:border-[#E0001B] hover:bg-[#E0001B]/5 transition-colors touch-manipulation"
              >
                <div className="w-10 h-10 rounded-full bg-[#E0001B]/10 flex items-center justify-center">
                  <Camera className="w-5 h-5 text-[#E0001B]" />
                </div>
                <span className="text-[10px] text-[#6B6B6B]">Take Photo</span>
              </button>
            )}
          </div>

          {photos.length === 0 && (
            <div className="mt-2 flex items-start gap-2 text-[#E02020]">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p className="text-[12px]">Take at least 1 photo of the package before confirming</p>
            </div>
          )}

          {error && (
            <p className="mt-2 text-[12px] text-[#E02020]">{error}</p>
          )}

          <p className="text-[12px] text-[#6B6B6B] mt-2">
            Opens your camera or gallery to add real pickup photos
          </p>
        </div>

        <div>
          <label htmlFor="notes" className="block text-[14px] text-[#111111] mb-2">
            Notes (Optional)
          </label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any special notes about the package..."
            rows={3}
            className="w-full px-4 py-3 rounded-[12px] border-2 border-gray-200 bg-white focus:border-[#E0001B] focus:outline-none transition-colors resize-none text-[14px]"
            maxLength={200}
          />
          <p className="text-[12px] text-[#6B6B6B] mt-1">{notes.length}/200 characters</p>
        </div>
      </div>

      <div
        className="shrink-0 p-4 border-t border-gray-200 bg-white"
        style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
      >
        <button
          type="button"
          onClick={handleConfirm}
          disabled={photos.length === 0 || isSubmitting}
          className="w-full h-12 rounded-[12px] bg-[#E0001B] text-white hover:bg-[#C00017] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 touch-manipulation"
        >
          <Check className="w-5 h-5" />
          {isSubmitting ? 'Uploading…' : 'Confirm Pickup'}
        </button>
      </div>
    </div>
  );
}
